(() => {
  'use strict';

  const initialized = window.__btdPosterMakers || (window.__btdPosterMakers = new WeakSet());
  const WIDTH = 1080;
  const FORMATS = {
    story: { height: 1920, ratio: '9 / 16', titleY: 1182, nameY: 1293, replyY: 1400, replySize: 72, replyHeight: 300, creditY: 1820 },
    feed: { height: 1350, ratio: '4 / 5', titleY: 970, nameY: 1068, replyY: 1162, replySize: 66, replyHeight: 210, creditY: 1320 }
  };

  function init(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    const $ = (selector) => root.querySelector(selector);
    const config = JSON.parse($('[data-btd-config]').textContent);
    const copy = config.ui || {};
    const ui = (key, fallback) => Object.hasOwn(copy, key) ? String(copy[key] ?? '') : fallback;
    const canvas = $('canvas');
    const ctx = canvas.getContext('2d');
    const name = $('[data-btd-name]');
    const reply = $('[data-btd-reply]');
    const downloadButton = $('[data-btd-download]');
    const shareButton = $('[data-btd-share]');
    const status = $('[data-btd-status]');
    const error = $('[data-btd-error]');
    const fallbackLink = $('[data-btd-open-image]');
    const formatButtons = root.querySelectorAll('[data-btd-format]');
    const background = new Image();
    const instanceId = 'btd-' + Math.random().toString(36).slice(2);
    let assetReady = false;
    let revision = 0;
    let preparedFile = null;
    let objectUrl = null;
    let busy = false;
    let renderTimer;
    let selectedFormat = Object.hasOwn(FORMATS, config.defaultFormat) ? config.defaultFormat : 'story';

    canvas.width = WIDTH;
    canvas.height = FORMATS[selectedFormat].height;
    background.crossOrigin = 'anonymous';

    const characters = (text) => Array.from(text);
    const cleanName = () => name.value.trim().replace(/\s+/gu, ' ').replace(/[?？]+$/gu, '');
    const cleanReply = () => {
      const paragraphs = reply.value.trim().split(/\r?\n/u).map((line) => line.trim()).filter(Boolean);
      if (paragraphs.length > 3) return paragraphs.slice(0, 2).concat(paragraphs.slice(2).join(' ')).join('\n');
      return paragraphs.join('\n');
    };
    const complete = () => Boolean(cleanName() && reply.value.trim());
    const fontStack = (kind) => kind === 'headline'
      ? '"' + instanceId + '-headline", "Arial Black", Arial, sans-serif'
      : '"' + instanceId + '-reply", cursive';

    async function loadFont(url, kind) {
      if (!url) throw new Error('A font URL is missing.');
      const safeUrl = new URL(url, document.baseURI).href;
      const weight = kind === 'headline' ? String(config.headlineWeight || 900) : '400';
      const font = new FontFace(instanceId + '-' + kind, 'url(' + JSON.stringify(safeUrl) + ')', { weight });
      await font.load();
      document.fonts.add(font);
    }

    function setButtons() {
      const disabled = !assetReady || !complete() || !preparedFile || busy;
      downloadButton.disabled = disabled;
      shareButton.disabled = disabled;
    }

    function fitHeadline(text, baseline, baseSize) {
      const stretch = Number(config.headlineStretch || 1);
      const weight = Number(config.headlineWeight || 900);
      let size = baseSize;
      ctx.font = weight + ' ' + size + 'px ' + fontStack('headline');
      const measured = ctx.measureText(text).width * stretch;
      if (measured > 850) size *= 850 / measured;
      ctx.save();
      ctx.translate(WIDTH / 2, baseline);
      ctx.scale(stretch, 1);
      ctx.font = weight + ' ' + size + 'px ' + fontStack('headline');
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = config.pink || '#f1b3c9';
      ctx.globalAlpha = 1;
      ctx.fillText(text, 0, 0);
      ctx.restore();
    }

    function wrapReply(text, maxWidth) {
      const lines = [];
      const segmenter = typeof Intl.Segmenter === 'function'
        ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;
      for (const paragraph of text.split('\n')) {
        let line = '';
        for (const word of paragraph.trim().split(/\s+/u)) {
          if (!word) continue;
          const next = line ? line + ' ' + word : word;
          if (ctx.measureText(next).width <= maxWidth) {
            line = next;
          } else {
            if (line) lines.push(line);
            line = '';
            const glyphs = segmenter ? Array.from(segmenter.segment(word), (part) => part.segment) : characters(word);
            for (const glyph of glyphs) {
              if (line && ctx.measureText(line + glyph).width > maxWidth) {
                lines.push(line);
                line = '';
              }
              line += glyph;
            }
          }
        }
        lines.push(line);
      }
      return lines;
    }

    function drawReply(text) {
      const layout = FORMATS[selectedFormat];
      let size = layout.replySize;
      let lines;
      let offsets;
      for (; size >= 28; size -= 1) {
        ctx.font = size + 'px ' + fontStack('reply');
        lines = wrapReply(text, 840);
        const metrics = lines.map((line) => ctx.measureText(line));
        offsets = [0];
        for (let index = 1; index < lines.length; index += 1) {
          // Leave space for real ascenders and descenders in the selected handwriting.
          const leading = Math.max(size * 1.14, metrics[index - 1].actualBoundingBoxDescent + metrics[index].actualBoundingBoxAscent + size * 0.12);
          offsets.push(offsets[index - 1] + leading);
        }
        const availableHeight = config.showCredit && selectedFormat === 'feed' ? 165 : layout.replyHeight;
        const bottom = layout.replyY + offsets.at(-1) + metrics.at(-1).actualBoundingBoxDescent;
        const top = layout.replyY - metrics[0].actualBoundingBoxAscent;
        const maxBottom = config.showCredit ? layout.creditY - 30 : layout.height - 30;
        if (size === 28 || (lines.length <= 3 && bottom - top <= availableHeight && top >= layout.nameY + 16 && bottom <= maxBottom)) break;
      }
      ctx.font = size + 'px ' + fontStack('reply');
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = '#242220';
      const firstBaseline = layout.replyY;
      lines.forEach((line, index) => ctx.fillText(line, WIDTH / 2, firstBaseline + offsets[index]));
    }

    function render() {
      revision += 1;
      const currentRevision = revision;
      preparedFile = null;
      setButtons();
      $('[data-btd-name-count]').textContent = characters(name.value).length + '/28';
      $('[data-btd-reply-count]').textContent = characters(reply.value).length + '/90';
      const layout = FORMATS[selectedFormat];
      if (canvas.height !== layout.height) canvas.height = layout.height;
      $('[data-btd-canvas-wrap]').style.aspectRatio = layout.ratio;
      $('[data-btd-dimensions]').textContent = WIDTH + ' × ' + layout.height + ' ' + ui('dimensions_unit', 'px');
      $('[data-btd-destination]').textContent = selectedFormat === 'feed' ? ui('feed_caption', 'Made for your feed ↗') : ui('story_caption', 'Made for your Story ↗');
      $('[data-btd-format-note]').textContent = selectedFormat === 'feed' ? ui('feed_note', 'Made for your feed. And his.') : ui('story_note', 'Made for your Story. And his.');
      formatButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.btdFormat === selectedFormat)));
      if (!assetReady) return;
      const posterName = cleanName() || ui('example_one_name', 'Andres');
      const posterReply = cleanReply() || ui('example_one_reply', 'He was a mess');
      const posterPrefix = ui('poster_prefix', 'Remember');
      ctx.clearRect(0, 0, WIDTH, layout.height);
      // Keep the original artwork in proportion. Feed trims only the lower red background.
      ctx.drawImage(background, 0, 0, WIDTH, 1920);
      fitHeadline(posterPrefix.toLocaleUpperCase(), layout.titleY, 100);
      fitHeadline(posterName.toLocaleUpperCase() + '?', layout.nameY, 100);
      drawReply(posterReply);
      if (config.showCredit) {
        ctx.font = '21px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = config.pink || '#f1b3c9';
        ctx.fillText((ui('footer_song', 'Boys These Days') + ' · ' + ui('footer_artists', 'Evangelia x Misha Miller')).toLocaleUpperCase(), WIDTH / 2, layout.creditY);
      }
      canvas.setAttribute('aria-label', posterPrefix + ' ' + posterName + '? ' + posterReply);
      $('[data-btd-preview-label]').textContent = complete() ? ui('preview_filled', 'Your poster') : ui('preview_empty', 'A little inspiration');
      clearTimeout(renderTimer);
      renderTimer = setTimeout(() => {
        try {
          canvas.toBlob((blob) => {
            if (!blob || revision !== currentRevision) return;
            const slug = posterName.normalize('NFKD').replace(/[\u0300-\u036f]/gu, '').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/gu, '').toLowerCase() || 'your-ex';
            preparedFile = new File([blob], 'remember-' + slug + '-' + selectedFormat + '.png', { type: 'image/png' });
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            objectUrl = URL.createObjectURL(blob);
            fallbackLink.href = objectUrl;
            setButtons();
          }, 'image/png');
        } catch (reason) {
          error.textContent = ui('export_error', 'The poster could not be exported. Please reload the page and try again.');
          error.hidden = false;
        }
      }, 100);
    }

    function saveFile() {
      if (!preparedFile || !complete() || busy) return;
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = preparedFile.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      status.textContent = selectedFormat === 'feed'
        ? ui('download_feed', 'Your poster is ready. Save it and post it to your feed.')
        : ui('download_story', 'Your poster is ready. Save it and share it to your Story.');
      fallbackLink.hidden = false;
    }

    function onEdit(event) {
      const field = event.target;
      const limit = field === name ? 28 : 90;
      if (characters(field.value).length > limit) field.value = characters(field.value).slice(0, limit).join('');
      status.textContent = '';
      fallbackLink.hidden = true;
      render();
    }

    name.addEventListener('input', onEdit);
    reply.addEventListener('input', onEdit);
    formatButtons.forEach((button) => button.addEventListener('click', () => {
      if (!Object.hasOwn(FORMATS, button.dataset.btdFormat) || button.dataset.btdFormat === selectedFormat) return;
      selectedFormat = button.dataset.btdFormat;
      status.textContent = '';
      fallbackLink.hidden = true;
      render();
    }));
    downloadButton.addEventListener('click', saveFile);
    shareButton.addEventListener('click', async () => {
      if (!preparedFile || !complete() || busy) return;
      if (!navigator.share || !navigator.canShare || !navigator.canShare({ files: [preparedFile] })) {
        saveFile();
        return;
      }
      const file = preparedFile;
      busy = true;
      setButtons();
      try {
        await navigator.share({ files: [file], title: ui('footer_song', 'Boys These Days'), text: ui('poster_prefix', 'Remember') + ' ' + cleanName() + '?' });
        status.textContent = ui('shared_message', 'Your poster is ready for its moment.');
      } catch (reason) {
        if (reason.name !== 'AbortError') {
          status.textContent = ui('share_error', 'Sharing is unavailable here. Use Download poster to save your image.');
          fallbackLink.hidden = false;
        }
      } finally {
        busy = false;
        setButtons();
      }
    });
    root.querySelectorAll('[data-btd-example]').forEach((button) => button.addEventListener('click', () => {
      const second = button.dataset.btdPreset === 'two';
      name.value = second ? ui('example_two_name', 'John') : ui('example_one_name', 'Andres');
      reply.value = second ? ui('example_two_reply', 'He was in love\n(with his mom)') : ui('example_one_reply', 'He was a mess');
      status.textContent = '';
      fallbackLink.hidden = true;
      render();
    }));
    $('[data-btd-reset]').addEventListener('click', () => {
      name.value = '';
      reply.value = '';
      status.textContent = '';
      fallbackLink.hidden = true;
      render();
      name.focus();
    });

    const imageLoaded = new Promise((resolve, reject) => {
      background.onload = resolve;
      background.onerror = reject;
    });
    background.src = config.backgroundUrl;
    render();
    Promise.all([imageLoaded, loadFont(config.headlineFontUrl, 'headline'), loadFont(config.replyFontUrl, 'reply')])
      .then(() => {
        assetReady = true;
        root.classList.add('btd-ready');
        render();
      })
      .catch(() => {
        error.textContent = ui('asset_error', 'The artwork or fonts could not load. Please reload the page to try again.');
        error.hidden = false;
        $('[data-btd-preview-label]').textContent = ui('preview_error', 'Preview unavailable');
      });
  }

  function scan(scope = document) {
    if (scope.matches && scope.matches('[data-btd-poster-maker]')) init(scope);
    scope.querySelectorAll('[data-btd-poster-maker]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
